import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { CircleInviteResponse } from "@consistency-circle/shared";
import { MailIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { apiClient } from "../../../lib/api";
import { circlesQueryKey } from "../hooks/useCircleData";
import { formatDateTime } from "../utils";

interface InviteRowProps {
  readonly circleId: string;
  readonly invite: CircleInviteResponse;
}

export function InviteRow({ circleId, invite }: InviteRowProps) {
  const queryClient = useQueryClient();

  const revoke = useMutation({
    mutationFn: () => apiClient.revokeCircleInvite(circleId, invite.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["circle-invites", circleId] });
      void queryClient.invalidateQueries({ queryKey: circlesQueryKey });
    },
  });

  return (
    <div className="flex items-center gap-3 px-4 py-3 text-sm">
      <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
        <MailIcon className="w-3.5 h-3.5 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{invite.email}</div>
        <div className="text-xs text-muted-foreground">Expires {formatDateTime(invite.expiresAt)}</div>
      </div>
      <Button
        variant="ghost"
        size="xs"
        className="text-muted-foreground hover:text-destructive shrink-0"
        onClick={() => revoke.mutate()}
        disabled={revoke.isPending}
      >
        {revoke.isPending ? "Cancelling..." : "Cancel"}
      </Button>
    </div>
  );
}
